import { guideStepTitle } from '@hue/contracts';
import type { GuideDefinition } from './guide-types.js';

const plural = (count: number, one: string, many: string) => (count === 1 ? one : many);
const money = (cents: number) =>
  (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const BRING = 'Trazer o que aconteceu';
const POST = 'Lançar o que falta';
const LINK = 'Ligar o banco ao que foi lançado';
const CLASSIFY = 'Dizer de que é cada dinheiro';
const CLOSE = 'Fechar e entregar';
const AFTER = 'Depois do fechamento';

/// O Fechamento do mês (ADR 0084), na ordem de trabalho da Hue. `{mes}` vira o mês do roteiro.
/// Cada nome de tela e de botão aqui é o da tela; mudou lá, muda aqui.
export const monthClosingGuide: GuideDefinition = {
  key: 'month-closing',
  title: 'Fechamento do mês',
  steps: [
    {
      key: 'sales',
      phase: BRING,
      title: guideStepTitle('month-closing', 'sales'),
      tag: 'silent',
      target: { screen: 'sales', label: 'Vendas' },
      where: 'Vendas',
      help: 'sales',
      summary:
        'O que não foi registrado aqui não entra no resultado, e o dinheiro dele fica sem receita para se ligar no extrato.',
      why: 'As vendas geram as receitas e as comissões que os próximos passos usam.',
      how: ['Abra Vendas e confira as vendas de {mes}.', 'Registre o que faltou.'],
      cases: [
        ['Veio de um agendamento', 'Conclua pela Agenda, em Fechamento do atendimento.'],
        ['Não passou pela agenda', 'Registre em Nova venda.'],
      ],
      effects: [['Receita do dia pronta para conciliar', 'positive']],
      action: 'Confira se todas as vendas de {mes} foram registradas.',
      confirmation: 'conferiu as vendas de {mes}',
    },
    {
      key: 'statement',
      phase: BRING,
      title: guideStepTitle('month-closing', 'statement'),
      tag: 'silent',
      target: { screen: 'finance', search: { tab: 'statement' }, label: 'Caixa e extrato' },
      where: 'Financeiro › Caixa e extrato, aba Extrato',
      help: 'finance',
      summary: 'Traga tudo o que passou pelo banco e pelo caixa no mês.',
      why: 'Sem o extrato não há o que conciliar, e o caixa do mês não mostra a realidade.',
      how: [
        'Escolha a conta financeira.',
        'Clique em Importar extrato, escolha o arquivo do banco e confira.',
        'Clique em Importar movimentos.',
        'Repita para cada conta financeira.',
      ],
      cases: [
        ['Conta financeira sem arquivo, como o caixa físico', 'Registre os movimentos à mão.'],
        [
          'Há regras de classificação ativas',
          'A importação já escolhe a conta do plano do que casar com elas.',
        ],
      ],
      problems: [
        [
          'Importou na conta financeira errada',
          'Em Importações do extrato, use Conferir importação e Desfazer importação. Depois importe na certa.',
        ],
        [
          'Importou o mesmo arquivo de novo',
          'O sistema protege contra repetir as mesmas linhas na mesma conta financeira.',
        ],
      ],
      effects: [['Movimentos no extrato, a tratar', 'info']],
      action: 'Importe o extrato de cada conta financeira, até o último dia de {mes}.',
      confirmation: 'importou o extrato de todas as contas financeiras',
    },
    {
      key: 'operator-report',
      phase: BRING,
      title: guideStepTitle('month-closing', 'operator-report'),
      tag: 'optional',
      target: { screen: 'payment-reports', label: 'Operadoras' },
      where: 'Financeiro › Operadoras',
      help: 'payment-reports',
      summary:
        'Separa o bruto da venda, a taxa retida e o líquido esperado na conta financeira configurada.',
      why: 'Assim a taxa entra no DRE no mês da venda, e não quando o repasse for classificado.',
      how: [
        'Clique em Selecionar arquivo Excel, escolha o arquivo e clique em Conferir prévia.',
        'Confira Bruto, Taxa, Líquido, Conta financeira e Repasses.',
        'Em Receita original, escolha a receita diária de Vendas correspondente. Se mudar um vínculo, clique em Atualizar prévia.',
        'Clique em Confirmar importação.',
      ],
      cases: [
        ['Ton', 'Use o relatório de vendas da própria operadora.'],
        ['Outra operadora', 'Clique em Baixar modelo Excel e preencha Transações e Repasses.'],
      ],
      problems: [
        [
          'Operadora ainda não configurada',
          'Clique em Configurar operadoras e escolha a conta financeira e o prazo.',
        ],
        [
          'Ficou sem Receita original',
          'A taxa e o líquido são registrados, mas nenhuma receita entra no DRE.',
        ],
        [
          'Transação já importada',
          'Retire do arquivo. A importação não absorve diferenças sozinha.',
        ],
      ],
      effects: [['Taxa no DRE no mês da venda', 'positive']],
      action: 'Importe o relatório de {mes} da operadora.',
      confirmation: 'importou o relatório da operadora de {mes}',
    },
    {
      key: 'expenses',
      phase: POST,
      title: guideStepTitle('month-closing', 'expenses'),
      tag: 'silent',
      target: { screen: 'payables', label: 'Despesas' },
      where: 'Financeiro › Despesas',
      help: 'payables',
      summary:
        'Recorrências variáveis, despesas avulsas e reembolsos a quem pagou com recursos pessoais.',
      why: 'A despesa precisa existir para o pagamento do extrato ter o que quitar.',
      how: [
        'Em Recorrências, use Informar valores do mês e Lançar valores informados para as variáveis. As fixas são lançadas automaticamente.',
        'Clique em Nova despesa para cada despesa avulsa.',
        'Guarde nota e cupom em Comprovantes, se quiser.',
      ],
      cases: [
        [
          'Compra paga com dinheiro pessoal',
          'Na despesa, escolha Recursos pessoais — a empresa vai reembolsar. Nada sai do banco no cadastro.',
        ],
        [
          'Movimento que não terá despesa ou receita cadastrada, como uma tarifa',
          'Na Conciliação, use Sem compromisso, informe o motivo e clique em Marcar. Se tiver Contabilidade gerencial, confira também a conta do plano e a competência em Classificar lançamentos.',
        ],
      ],
      problems: [['A despesa veio de Compras', 'Ela só é alterada em Compras.']],
      effects: [['Despesa em aberto, esperando o pagamento', 'info']],
      action: 'Lance as recorrências variáveis, as despesas avulsas e os reembolsos de {mes}.',
      confirmation: 'lançou as despesas de {mes}',
    },
    {
      key: 'purchases',
      phase: POST,
      title: guideStepTitle('month-closing', 'purchases'),
      tag: 'optional',
      target: { screen: 'purchasing', label: 'Compras' },
      where: 'Compras',
      help: 'purchasing',
      summary: 'A compra recebida vira despesa e, se tiver produto, entrada no estoque.',
      why: 'Cadastrar a despesa à mão e também receber em Compras contaria o mesmo gasto duas vezes.',
      how: [
        'Em Compras, registre o recebimento do que chegou no mês.',
        'A despesa aparece em Financeiro › Despesas com origem Compras.',
      ],
      problems: [
        ['Fornecedor ou produto sem cadastro', 'Cadastre antes de receber.'],
        [
          'Precisa mudar a despesa da compra',
          'Altere em Compras: a despesa criada pela compra só muda lá.',
        ],
      ],
      effects: [['Despesa com origem Compras', 'positive']],
      action: 'Registre o recebimento do que chegou em {mes}.',
      confirmation: 'registrou as compras recebidas em {mes}',
    },
    {
      key: 'commissions',
      phase: POST,
      title: guideStepTitle('month-closing', 'commissions'),
      tag: 'warns',
      target: { screen: 'commissions', label: 'Comissões' },
      where: 'Comissões',
      help: 'commissions',
      summary:
        'A apuração mostra comissão, vales e líquido de cada profissional, e gera uma despesa para cada um.',
      why: 'O Pix da comissão só se liga à despesa depois da apuração. Por isso vem antes de conciliar.',
      how: [
        'Escolha o mês e confira cada profissional. Exportar tudo gera a planilha para o contador.',
        'Clique em Apurar comissões do mês mostrado, escolha o vencimento e clique em Gerar despesas.',
      ],
      cases: [
        [
          'Profissional recebeu vale',
          'A despesa continua pelo valor cheio, e o vale reduz o que falta pagar.',
        ],
        [
          'Entrou comissão depois de apurar',
          'Apure de novo: cada apuração pega só o que ainda não foi apurado.',
        ],
      ],
      problems: [
        [
          'Apurou errado',
          'Em Apurações deste mês, use Desfazer apuração. Vale para a mais recente da unidade, sem pagamento conciliado e com o mês aberto.',
        ],
        [
          'Ficou comissão sem apurar',
          'O Fechamento avisa. Depois de fechado, ela entra na apuração de um mês seguinte, e o custo cai naquele mês.',
        ],
      ],
      effects: [['Uma despesa por profissional', 'positive']],
      action: 'Confira e apure as comissões de {mes}.',
      confirmation: 'conferiu e apurou as comissões de {mes}',
      measure: {
        done: (facts) =>
          facts.pendingCommissionsCents === undefined ? null : facts.pendingCommissionsCents === 0,
        todo: (facts) =>
          `Apure os ${money(facts.pendingCommissionsCents ?? 0)} de comissão que faltam.`,
        ok: 'Nenhuma comissão sem apurar.',
      },
    },
    {
      key: 'reconciliation',
      phase: LINK,
      title: guideStepTitle('month-closing', 'reconciliation'),
      tag: 'blocks',
      target: { screen: 'finance', search: { tab: 'reconciliation' }, label: 'Conciliação' },
      where: 'Financeiro › Caixa e extrato, aba Conciliação',
      help: 'finance',
      summary:
        'Diga o que cada movimento do banco quitou. Conciliar dá a despesa como paga e a receita como recebida.',
      why: 'O caixa só fecha com todo movimento conciliado ou marcado como sem compromisso.',
      how: [
        'No movimento, clique em Conciliar.',
        'Escolha a despesa ou a receita que ele quitou. Pode ser mais de uma.',
        'Confira Vai quitar e Sobra antes de confirmar.',
        'Trate a diferença, se o valor não fechar.',
      ],
      cases: [
        ['Pagamento menor que a despesa', 'O restante continua em aberto.'],
        [
          'Um Pix quitou várias parcelas',
          'Marque todas: um movimento pode quitar compras diferentes.',
        ],
        [
          'Dinheiro passou de uma conta financeira para outra',
          'No Extrato, use Transferir entre contas. Transferência não é despesa nem receita.',
        ],
        ['Repasse da operadora', 'Concilie com o que foi importado, sem criar receita de novo.'],
      ],
      problems: [
        [
          'Ligou ao item errado',
          'Na aba Conciliação, clique em Desfazer. O valor volta a ficar em aberto na origem.',
        ],
        [
          'Não existe despesa para este movimento',
          'Cadastre em Despesas se houver um compromisso a quitar. Caso contrário, use Sem compromisso na Conciliação, informe o motivo e clique em Marcar. Com Contabilidade gerencial, confira também a conta do plano e a competência em Classificar lançamentos.',
        ],
      ],
      effects: [
        ['Despesa dada como paga na origem', 'positive'],
        ['Receita dada como recebida', 'positive'],
      ],
      action: 'Concilie todos os movimentos de {mes}.',
      confirmation: 'conciliou os movimentos de {mes}',
      measure: {
        done: (facts) =>
          facts.movementsWithoutTreatment === undefined
            ? null
            : facts.movementsWithoutTreatment === 0,
        todo: (facts) => {
          const count = facts.movementsWithoutTreatment ?? 0;
          return `Trate ${plural(count, 'o movimento que falta', `os ${String(count)} movimentos que faltam`)}: concilie ou declare sem compromisso.`;
        },
        ok: 'Nenhum movimento sem tratamento.',
      },
    },
    {
      key: 'classification',
      phase: CLASSIFY,
      title: guideStepTitle('month-closing', 'classification'),
      tag: 'warns',
      target: { screen: 'classification', label: 'Classificar lançamentos' },
      where: 'Contabilidade gerencial › Classificar lançamentos',
      help: 'classification',
      summary:
        'Conciliar diz o que um pagamento quitou. Classificar diz de quê é o dinheiro: aluguel, insumos, marketing.',
      why: 'É o que coloca cada valor na linha certa do DRE. O valor sem conta do plano pesa no resultado, mas não aparece em nenhuma linha.',
      how: [
        'Abra Classificar lançamentos e escolha a conta do plano de cada linha Sem conta.',
        'Escolha Extrato no alto para ver o que veio do banco sem despesa cadastrada.',
        'Para repetir uma escolha nos próximos meses, crie uma regra em Regras de classificação.',
      ],
      cases: [
        [
          'Saiu do banco e nunca vai pagar despesa cadastrada',
          'Clique em Declarar sem compromisso, escreva o Motivo, confira o Mês de competência, escolha a conta do plano e clique em Declarar.',
        ],
        [
          'Linha com a etiqueta Aguardando conciliação',
          'Só o que sobrou do pagamento aparece. A conta do plano escolhida vale só no Fluxo de caixa, até o resto ser resolvido.',
        ],
        [
          'Existe regra, mas a linha chegou antes dela',
          'Em Regras de classificação, clique em Aplicar aos sem conta.',
        ],
      ],
      problems: [
        ['Errou o mês', 'Clique em Alterar competência na linha.'],
        ['Errou a declaração', 'Desfazer declaração devolve a linha a Aguardando conciliação.'],
      ],
      effects: [
        ['Sem compromisso: conta no DRE', 'positive'],
        ['Aguardando: conta só no caixa', 'info'],
      ],
      action: 'Escolha a conta do plano de tudo o que está sem conta do plano em {mes}.',
      confirmation: 'classificou os lançamentos de {mes}',
      measure: {
        done: (facts) =>
          facts.unclassifiedCount === undefined ? null : facts.unclassifiedCount === 0,
        todo: (facts) => {
          const count = facts.unclassifiedCount ?? 0;
          const value = facts.noAccountCents ?? 0;
          return `Escolha a conta do plano ${plural(count, 'do lançamento pendente', `dos ${String(count)} lançamentos pendentes`)}${value > 0 ? `, no total de ${money(value)}` : ''}.`;
        },
        ok: 'Nenhum valor sem conta do plano.',
      },
    },
    {
      key: 'cash-closing',
      phase: CLOSE,
      title: guideStepTitle('month-closing', 'cash-closing'),
      tag: 'blocks',
      target: { screen: 'finance', search: { tab: 'closing' }, label: 'Fechamento do caixa' },
      where: 'Financeiro › Caixa e extrato, aba Fechamento',
      help: 'finance',
      summary: 'Confirma que o extrato de {mes} foi todo tratado e trava os movimentos do mês.',
      why: 'O Fechamento contábil depende do caixa fechado.',
      how: [
        'Confira o saldo de cada conta financeira em Caixa do mês.',
        'Veja Movimentos sem tratamento. Se houver, clique em Ir para a conciliação.',
        'Clique em Fechar mês.',
      ],
      cases: [
        [
          'Há despesa ou receita em aberto',
          'Não impede fechar: aparece em Compromissos do mês, como aviso.',
        ],
        ['Empresa com várias unidades', 'Cada unidade fecha o próprio caixa.'],
      ],
      problems: [
        [
          'Precisa mexer depois de fechar',
          'Clique em Reabrir mês e informe o motivo. Se o mês já foi fechado na Contabilidade, reabra lá primeiro.',
        ],
      ],
      effects: [['Movimentos do mês travados', 'info']],
      action: 'Clique em Fechar mês na aba Fechamento.',
      confirmation: 'fechou o caixa de {mes}',
      measure: {
        done: (facts) => facts.cashClosed ?? null,
        todo: (facts) => {
          const count = facts.movementsWithoutTreatment ?? 0;
          return count > 0
            ? `O caixa ainda não pode fechar: ${plural(count, 'falta 1 movimento', `faltam ${String(count)} movimentos`)}, no passo Conciliar.`
            : 'Clique em Fechar mês na aba Fechamento.';
        },
        ok: 'Caixa do mês fechado.',
      },
    },
    {
      key: 'reports-review',
      phase: CLOSE,
      title: guideStepTitle('month-closing', 'reports-review'),
      tag: 'read',
      target: { screen: 'dre', label: 'DRE' },
      where: 'Contabilidade gerencial › DRE',
      help: 'dre',
      summary: 'Última olhada nos números, ainda como prévia, antes de virarem oficiais.',
      why: 'Depois de fechar, mudar o mês pede reabrir ou lançar ajuste.',
      how: [
        'Abra o DRE, localize a coluna de {mes} e confira as receitas, despesas e o resultado. O resumo Resultado do período é acumulado de janeiro até o mês escolhido.',
        'Clique num valor que estranhar: abre ao lado a lista do que forma aquele número.',
        'Abra o Fluxo de caixa e confira o Saldo no fim do mês com o banco.',
      ],
      cases: [
        ['Valor em Sem conta do plano', 'Já pesa no resultado. Volte a Classificar lançamentos.'],
        [
          'Valor em Aguardando conciliação',
          'Fica fora do resultado. Concilie ou declare sem compromisso.',
        ],
      ],
      effects: [['Nada muda: é só leitura', 'neutral']],
      action: 'Leia o DRE e confira o Saldo no fim do mês do Fluxo de caixa com o banco.',
      confirmation: 'conferiu o DRE e o Fluxo de caixa de {mes}',
    },
    {
      key: 'accounting-closing',
      phase: CLOSE,
      title: guideStepTitle('month-closing', 'accounting-closing'),
      tag: 'final',
      target: { screen: 'accounting-closing', label: 'Fechamento contábil' },
      where: 'Contabilidade gerencial › Fechamento',
      help: 'accounting-closing',
      summary:
        'Transforma o DRE do mês em número oficial: o sistema guarda uma fotografia dele, conta do plano por conta do plano.',
      why: 'É o último passo que muda dados. Os meses fecham em ordem e só depois de acabar.',
      how: [
        'Em O que impede fechar, confira o caixa e responda os valores que podem estar contados duas vezes.',
        'Em Para conferir, veja o que está sem conta do plano, aguardando conciliação ou sem apurar. Para fechar assim mesmo, ligue a chave “Sei que esses valores existem”.',
        'Confira O que fica em aberto e Resultado que será guardado.',
        'Clique em Fechar {mes} e confirme no diálogo.',
      ],
      cases: [
        [
          'Valor contado duas vezes',
          'Responda “É o mesmo dinheiro: contar uma vez” ou “São recebimentos diferentes: contar os dois”.',
        ],
        [
          'Primeiro mês da empresa',
          'O primeiro mês a fechar é escolha sua; depois dele, os meses fecham em ordem.',
        ],
        [
          'Várias unidades',
          'Cada uma fecha o próprio mês. O DRE somado só fica oficial quando todas fecharem.',
        ],
      ],
      problems: [
        ['Aparece Espera o mês anterior', 'Feche antes o mês anterior da mesma unidade.'],
        [
          'Aparece Em andamento',
          'O mês ainda não acabou. Ele só fecha a partir do primeiro dia do mês seguinte.',
        ],
      ],
      effects: [
        ['DRE do mês passa a mostrar “fechado”', 'positive'],
        ['Mexer no mês passa a pedir reabrir ou ajuste', 'warning'],
      ],
      action: 'Faça o Fechamento contábil de {mes}.',
      confirmation: 'fez o Fechamento contábil de {mes}',
      measure: {
        done: (facts) =>
          facts.accountingStatus === undefined ? null : facts.accountingStatus === 'CLOSED',
        todo: (facts, monthName) => {
          if (facts.accountingStatus === 'WAITING')
            return 'Feche antes o mês anterior desta unidade.';
          if (facts.accountingStatus === 'RUNNING' || facts.accountingStatus === 'FUTURE')
            return 'O mês ainda não acabou. Ele só fecha a partir do primeiro dia do mês seguinte.';
          if (facts.accountingStatus === 'BLOCKED')
            return 'Resolva o que aparece em O que impede fechar.';
          return `Clique em Fechar ${monthName} e confirme no diálogo.`;
        },
        ok: 'Mês fechado na Contabilidade.',
      },
    },
    {
      key: 'send-reports',
      phase: CLOSE,
      title: guideStepTitle('month-closing', 'send-reports'),
      tag: 'optional',
      target: { screen: 'dre', label: 'DRE' },
      where: 'Contabilidade gerencial › DRE',
      help: 'dre',
      summary:
        'Exporte os números após fechar o mês. O DRE leva o acumulado de janeiro até o mês escolhido, com uma coluna por mês.',
      why: 'Enviado antes de fechar, o número ainda pode mudar.',
      how: [
        'Confira o mês de trabalho no topo. No DRE, clique em Exportar e confira o período e a unidade descritos no diálogo.',
        'Escolha Planilha Excel ou PDF e clique em Baixar arquivo.',
        'Na planilha, ligue Incluir os lançamentos de cada valor para o contador conferir de onde veio cada número.',
      ],
      effects: [['Arquivo com o recorte da tela', 'positive']],
      action: 'Exporte o DRE acumulado até {mes}, confira o recorte do arquivo e envie.',
      confirmation: 'enviou os números de {mes}',
    },
    {
      key: 'fix-closed-month',
      phase: AFTER,
      title: guideStepTitle('month-closing', 'fix-closed-month'),
      tag: 'extra',
      appendix: true,
      target: { screen: 'accounting-closing', label: 'Fechamento contábil' },
      where: 'Contabilidade gerencial › Fechamento',
      help: 'accounting-closing',
      summary:
        'Ao salvar algo de um mês fechado, aparece um diálogo. Nada foi salvo ainda, e há dois caminhos.',
      how: [
        'Leia o diálogo que avisa que o mês está fechado.',
        'Escolha um dos dois caminhos abaixo, conforme os números já tenham saído da empresa ou não.',
      ],
      cases: [
        [
          'Os números já foram enviados',
          'Escreva o Motivo do ajuste e clique em Salvar e lançar ajuste. A diferença entra no mês de hoje, numa linha própria do DRE.',
        ],
        [
          'Os números ainda não saíram da empresa',
          'Clique em Ir para o Fechamento e em Reabrir o mês, com motivo. Corrija e feche de novo.',
        ],
        [
          'A tela mostra “mudou depois de fechado”',
          'Veio de Vendas, Compras, Investimento inicial ou da operadora. Abra o mês e escolha Lançar a diferença como ajuste ou Reabrir.',
        ],
      ],
      problems: [
        [
          'Reabrir um mês com o seguinte já fechado',
          'Reabre os dois. Ajustes lançados por causa deles são cancelados.',
        ],
      ],
      effects: [['Tudo fica na Auditoria', 'neutral']],
      action: 'Use só se alguém precisar mudar um mês já fechado.',
    },
  ],
};
